// src/features/projects/screens/ProjectsScreen.tsx

import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ProjectCard from '../../home/components/ProjectCard';
import styles from './ProjectsScreen.module.scss';
import { Project, Projects } from '../types/project';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';

// Import local thumbnail images
import lasemaineImage from '../../../shared/assets/images/project-thumbnails/lasemaine.png';
import alvoImage from '../../../shared/assets/images/project-thumbnails/alvo.png';
import amurabiImage from '../../../shared/assets/images/project-thumbnails/amurabi.png';
import tunisrhImage from '../../../shared/assets/images/project-thumbnails/tunisrh.png';
import bookmyroomImage from '../../../shared/assets/images/project-thumbnails/bookmyroom.jpg';
import quoteFlowImage from '../../../shared/assets/images/project-thumbnails/Flitter-quote.png';

const ProjectsScreen: React.FC = () => {
  const { t, i18n } = useTranslation('projects');
  const navigate = useNavigate();

  const projectsData = i18n.getResourceBundle(i18n.language, 'projects') as Projects;
  const [hoveredProject, setHoveredProject] = useState<Project | null>(null);

  // Image mapping for local thumbnails
  const imageMap: { [key: string]: string } = {
    'la-semaine': lasemaineImage,
    'alvo-marketplace': alvoImage,
    'amurabi-poc': amurabiImage,
    'tunisrh': tunisrhImage,
    'bookmyroom': bookmyroomImage,
    'quote-flow': quoteFlowImage,
  };

  // Function to get the correct image (local if available, fallback to external)
  const getProjectImage = (project: Project): string => {
    return imageMap[project.id] || project.hero.image;
  };

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleMouseEnter = (project: Project) => {
    setHoveredProject(project);
  };

  const handleProjectClick = (project: Project) => {
    // Navigate to the detail screen
    navigate(`/projects/${project.id}`);
  };

  // Framer Motion variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        when: 'beforeChildren',
        staggerChildren: 0.1,
      },
    },
    exit: { opacity: 0 },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 },
  };

  const imageVariants = {
    hidden: { opacity: 0, scale: 0.95 },
    visible: { opacity: 1, scale: 1 },
  };

  if (!projectsData || !projectsData.projects) {
    return <div>Loading...</div>;
  }

  return (
    <motion.div
      className={styles.projectsScreen}
      variants={containerVariants}
      initial="hidden"
      animate="visible"
      exit="exit"
    >
      <div className={styles.contentRow}>
        <div className={styles.projectShowScreen}>
          {hoveredProject ? (
            <motion.img
              key={hoveredProject.id}
              src={getProjectImage(hoveredProject)}
              alt={hoveredProject.hero.title}
              variants={imageVariants}
              initial="hidden"
              animate="visible"
              exit="hidden"
            />
          ) : (
            <div className={styles.emptyState}>
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
              >
                <h2>Hover on a project</h2>
              </motion.div>
            </div>
          )}
        </div>

        <div className={styles.projectsList}>
          <motion.ul
            className={styles.projectList}
            variants={containerVariants}
            initial="hidden"
            animate="visible"
          >
            {projectsData.projects.map((project) => (
              <motion.div
                key={project.id}
                onMouseEnter={() => handleMouseEnter(project)}
                onClick={() => handleProjectClick(project)}
                variants={itemVariants}
                whileHover={{ scale: 1.02 }}
                style={{ cursor: 'pointer' }}
              >
                <ProjectCard
                  title={project.hero.title}
                  description={project.hero.subtitle}
                  image={getProjectImage(project)}
                  // We do NOT pass projectUrl here, so no external link is rendered
                  technologies={project.technologies.map((tech) => tech.name)}
                />
              </motion.div>
            ))}
          </motion.ul>
        </div>
      </div>
    </motion.div>
  );
};

export default ProjectsScreen;